export const mapScormResources = (scormManifest) => {
  const resources = scormManifest.resources[0].resource.map((res) => {
    return res.$;
  }).filter((res) => {
    // SCORM 1.2 uses 'scormtype' with no caps:
    const scormType = res['adlcp:scormType'] || res['adlcp:scormtype'];
    return scormType === 'sco' || scormType === 'asset';
  });
  const organization = scormManifest.organizations[0].organization[0];

  scormManifest.sequencingCollection = parseSequencingCollection(scormManifest);
  scormManifest.controlMode = parseControlMode(organization);
  const scormItems = [];
  for (let i = 0; i < organization.item.length; i++) {
    const item = organization.item[i];
    scormItems.push(mapScormSubitems(item, resources, scormManifest));
  }
  return scormItems;
};

const mapScormSubitems = (item, resources, scormManifest) => {
  const resultItem = {
    title: item?.title?.[0],
    objectives: mapScormSequencingObjectives(item),
    sequencingRules: mapScormSequencingRules(item, scormManifest),
    navigationUI: mapScormNavigationInterface(item)
  };
  if (item?.$?.identifierref) {
    const refUrl = resources.find((res) => {
      return res.identifier === item.$.identifierref;
    });
    resultItem.url = refUrl?.href;
    if (item?.$?.parameters) {
      resultItem.url += item.$.parameters;
    }
  } else if (item?.item?.length) {
    const subItems = [];
    for (let i = 0; i < item.item.length; i++) {
      const subItem = item.item[i];
      subItems.push(mapScormSubitems(subItem, resources, scormManifest));
    }
    resultItem.subItems = subItems;
  }
  return resultItem;
};

const mapScormSequencingRules = (item, manifest) => {
  if (item?.['imsss:sequencing']?.[0]) {
    const seq = item?.['imsss:sequencing']?.[0];
    const sequencingRules = {};

    const preConditionRule = parsePreconditionRules(seq);
    if (preConditionRule) {
      sequencingRules.preConditionRule = preConditionRule;
    }

    sequencingRules.deliveryControls = parseDeliveryControls(seq, manifest);
    sequencingRules.rollupRules = getRollupRules(seq, manifest);
    return sequencingRules;
  }
};

const parseSequencingCollection = (manifest) => {
  const sequencingCollection = manifest['imsss:sequencingCollection'];
  const resultCollection = {};
  if (sequencingCollection) {
    for (let i = 0; i < sequencingCollection.length; i++) {
      const collectionSequencing = sequencingCollection?.[i]?.['imsss:sequencing']?.[0];
      if (collectionSequencing?.$?.ID) {
        const sequencingID = collectionSequencing.$.ID;
        const sequencingRule = {};
        const deliveryControls = collectionSequencing['imsss:deliveryControls'];
        if (deliveryControls?.[0]?.$) {
          sequencingRule.completionSetByContent = deliveryControls[0].$.completionSetByContent;
          sequencingRule.objectiveSetByContent = deliveryControls[0].$.objectiveSetByContent;
        }
        sequencingRule.rollupRules = parseRollupRules(collectionSequencing);
        resultCollection[sequencingID] = sequencingRule;
      }
    }
  }
  return resultCollection;
};

const parsePreconditionRules = (seq) => {
  const preConditionRule = {
    conditions: []
  };
  if (seq?.['imsss:sequencingRules']?.[0]) {
    const ruleConditions = seq?.['imsss:sequencingRules']?.[0]?.['imsss:preConditionRule']?.[0]?.['imsss:ruleConditions']?.[0];
    const ruleAction = seq?.['imsss:sequencingRules']?.[0]?.['imsss:preConditionRule']?.[0]?.['imsss:ruleAction']?.[0];
    preConditionRule.conditionCombination = ruleConditions?.$?.conditionCombination;
    if (ruleConditions?.['imsss:ruleCondition']) {
      const conditions = ruleConditions['imsss:ruleCondition'];
      for (const condition of conditions) {
        preConditionRule.conditions.push(condition.$);
      }
    }
    preConditionRule.action = ruleAction?.$?.action;
  }
  return preConditionRule.conditions.length ? preConditionRule : null;
};

const parseRollupRules = (seq) => {
  const rollupRules = seq['imsss:rollupRules'];
  if (rollupRules?.[0]?.$) {
    const rules = rollupRules[0].$;
    const rollupRule = {};
    rollupRule.objectiveMeasureWeight = rules.objectiveMeasureWeight;
    rollupRule.rollupObjectiveSatisfied = rules.rollupObjectiveSatisfied;
    rollupRule.rollupProgressCompletion = rules.rollupProgressCompletion;
    return rollupRule;
  }
  return null;
};

const getRollupRules = (seq, manifest) => {
  const rollupRules = parseRollupRules(seq, manifest);
  if (rollupRules) {
    return rollupRules;
  } else if (seq?.$?.IDRef && manifest?.sequencingCollection) {
    const idRef = seq.$.IDRef;
    const sequencingRules = manifest.sequencingCollection[idRef];
    if (sequencingRules?.rollupRules) {
      return sequencingRules.rollupRules;
    }
  }
  // else return default values
  return {
    objectiveMeasureWeight: '1',
    rollupObjectiveSatisfied: 'true',
    rollupProgressCompletion: 'true'
  };
};

const parseDeliveryControls = (seq, manifest) => {
  if (seq?.$?.IDRef && manifest?.sequencingCollection) {
    const idRef = seq.$.IDRef;
    const collection = manifest.sequencingCollection[idRef];
    if (collection) {
      const deliveryControls = {
        completionSetByContent: collection.completionSetByContent,
        objectiveSetByContent: collection.objectiveSetByContent
      };
      return deliveryControls;
    }
  }
  return null;
};

const mapScormNavigationInterface = (item) => {
  if (item?.['adlnav:presentation']?.[0]) {
    const navigationInterface = {};
    const hideLMSUI = item['adlnav:presentation'][0]?.['adlnav:navigationInterface']?.[0]?.['adlnav:hideLMSUI'];
    for (let i = 0; i < hideLMSUI.length; i++) {
      if (hideLMSUI[i] === 'continue') {
        navigationInterface.hideContinue = 'true';
      } else if (hideLMSUI[i] === 'previous') {
        navigationInterface.hidePrevious = 'true';
      }
    }
    return navigationInterface;
  }
};

const mapScormSequencingObjectives = (item) => {
  if (item?.['imsss:sequencing']?.[0]) {
    const sequencingControls = {
      objectives: []
    };
    const sequencing = item['imsss:sequencing'][0];
    if (sequencing) {
      sequencingControls.deliveryControls = sequencing?.['imsss:deliveryControls']?.[0]?.$;
    }
    if (sequencing?.['imsss:objectives']?.[0]) {
      const allObjectives = sequencing?.['imsss:objectives']?.[0];
      const primaryObjective = allObjectives['imsss:primaryObjective'];
      sequencingControls.primaryObjective = primaryObjective?.map((objective) => {
        if (objective?.$) {
          const targetObjectiveID = objective?.['imsss:mapInfo']?.[0]?.$?.targetObjectiveID;
          return {
            primary: true,
            id: objective?.$?.objectiveID,
            satisfiedByMeasure: objective?.$?.satisfiedByMeasure,
            minNormalizedMeasure: objective?.['imsss:minNormalizedMeasure']?.[0],
            targetObjectiveID: targetObjectiveID
          };
        }
        return null;
      }).filter((obj) => {
        return obj;
      });
      const objectives = allObjectives['imsss:objective'];
      if (objectives) {
        sequencingControls.objectives = objectives?.map((objective) => {
          if (objective.$) {
            const targetObjectiveID = objective?.['imsss:mapInfo']?.[0]?.$?.targetObjectiveID;
            return {
              id: objective?.$?.objectiveID,
              targetObjectiveID
            };
          }
          return null;
        });
      }
      sequencingControls.objectives = sequencingControls.primaryObjective.concat(sequencingControls.objectives);
    }
    return sequencingControls;
  }
};

const parseControlMode = (organization) => {
  let controlMode;
  if (organization) {
    const seq = organization?.['imsss:sequencing']?.[0];
    controlMode = seq?.['imsss:controlMode']?.$;
  }
  return controlMode;
};

export const mapScormObjectList = (scormContents) => {
  let itemList = [];
  for (let i = 0; i < scormContents.length; i++) {
    itemList = itemList.concat(getSubItems(scormContents[i], 1));
  }
  return itemList;
};

const getSubItems = (SCO, depth) => {
  let SCOList = [];
  SCOList.push({
    title: SCO.title,
    url: SCO.url,
    objectives: SCO.objectives,
    sequencingRules: SCO.sequencingRules,
    navigationUI: SCO.navigationUI,
    level: depth
  });
  if (SCO.subItems) {
    for (let i = 0; i < SCO.subItems.length; i++) {
      SCOList = SCOList.concat(getSubItems(SCO.subItems[i], depth + 1));
    }
  }
  return SCOList;
};
